/**
 * Centralized API configuration.
 * All API endpoints and the USDC mint used across the app resolve here.
 */
import type { MintAddress } from "@ellipsis-labs/rise";
import { isAddress } from "@solana/kit";
import {
  deriveWsUrlFromApi,
  normalizeWsUrl,
  toDevProxyWsUrl,
} from "./devProxy";
import { shouldUseClientDevProxy } from "./phoenixDevProxyConfig";

const deriveRpcSubscriptionsUrl = (rpcUrl: string): string => {
  if (!rpcUrl) return "";
  if (rpcUrl.startsWith("ws")) return rpcUrl;
  return rpcUrl.replace(/^http/, "ws");
};

const stripTrailingSlash = (url: string): string => url.replace(/\/+$/, "");
const parseEnabledFlag = (value: string | undefined): boolean => {
  const normalized = value?.trim().toLowerCase();
  return (
    normalized === "true" ||
    normalized === "1" ||
    normalized === "on" ||
    normalized === "enabled"
  );
};

const ENV_INFRA = {
  apiUrl: stripTrailingSlash(process.env.NEXT_PUBLIC_API_URL ?? ""),
  apiKey: process.env.NEXT_PUBLIC_PHOENIX_API_KEY ?? "",
  rpcUrl: process.env.NEXT_PUBLIC_RPC_URL ?? "",
  rpcSubscriptionsUrl:
    process.env.NEXT_PUBLIC_RPC_SUBSCRIPTIONS_URL ??
    deriveRpcSubscriptionsUrl(process.env.NEXT_PUBLIC_RPC_URL ?? ""),
  wsUrl:
    normalizeWsUrl(process.env.NEXT_PUBLIC_WS_URL ?? "") ||
    deriveWsUrlFromApi(process.env.NEXT_PUBLIC_API_URL ?? ""),
} as const;

const getClientDevProxyOrigin = (): string | null => {
  if (typeof window === "undefined") return null;

  const { hostname, origin } = window.location;
  const isLocalhost =
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname === "0.0.0.0";

  return shouldUseClientDevProxy() || isLocalhost ? origin : null;
};

/**
 * In dev-proxy mode, return the page origin so SDK HTTP fetches
 * go through Next.js's rewrite proxy (configured in `next.config.ts`). That
 * sidesteps CORS, which the Phoenix API doesn't grant to localhost. The
 * production app and SSR keep using the configured upstream URL directly.
 * In production / SSR / on the server, return the upstream API URL directly.
 */
export const getApiUrl = (): string => {
  const origin = getClientDevProxyOrigin();
  if (origin !== null) return origin;
  return ENV_INFRA.apiUrl;
};

/** The unproxied upstream URL — for server-to-server fetches that don't
 * need to go through the dev proxy. */
export const getUpstreamApiUrl = (): string => ENV_INFRA.apiUrl;
export const getSponsorshipUrl = (): string => getApiUrl();
export const isSponsorshipEnabled = (): boolean =>
  parseEnabledFlag(process.env.NEXT_PUBLIC_SPONSORSHIP_ENABLED);
export const getMakerFeeDistributorPubkey = (): string | null => {
  const pubkey = process.env.NEXT_PUBLIC_MAKER_FEE_DISTRIBUTOR_PUBKEY?.trim();
  return pubkey !== undefined && isAddress(pubkey) ? pubkey : null;
};
export const getApiKey = (): string => ENV_INFRA.apiKey;
export const getRpcUrl = (): string => ENV_INFRA.rpcUrl;
export const getRpcSubscriptionsUrl = (): string =>
  ENV_INFRA.rpcSubscriptionsUrl;
export const getWsUrl = (): string => {
  const origin = getClientDevProxyOrigin();
  if (origin !== null) return toDevProxyWsUrl(origin);
  return ENV_INFRA.wsUrl;
};

export const getExplorerTxUrl = (signature: string): string =>
  `https://solscan.io/tx/${signature}`;

export const openExplorerTx = (signature: string): void => {
  const url = getExplorerTxUrl(signature);
  const opened = window.open(url, "_blank", "noopener,noreferrer");
  if (opened === null) {
    window.location.assign(url);
  }
};

export const getServerRpcUrl = (): string => {
  const rpcUrl = process.env.RPC_URL ?? process.env.NEXT_PUBLIC_RPC_URL;
  if (rpcUrl !== undefined && rpcUrl !== null && rpcUrl !== "") {
    return rpcUrl;
  }
  throw new Error("Missing RPC_URL or NEXT_PUBLIC_RPC_URL");
};

// Nominal-branded mint address. The runtime check is just a non-empty-string
// guard — upstream (e.g. withdrawal flows) will reject on-chain if this is
// actually wrong.
// eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
export const USDC_MINT_ADDRESS = (process.env.NEXT_PUBLIC_USDC_MINT ??
  "EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v") as MintAddress;
